import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import gmailLogo from "@/assets/integrations/gmail.webp";
import salesforceLogo from "@/assets/integrations/salesforce.webp";
import zendeskLogo from "@/assets/integrations/zendesk.webp";
import calendlyLogo from "@/assets/integrations/calendly.webp";
import office365Logo from "@/assets/integrations/office365.webp";
import mondayLogo from "@/assets/integrations/monday.webp";
import hubspotLogo from "@/assets/integrations/hubspot.webp";
import {
  Headphones,
  Target,
  CalendarCheck,
  Cog,
  ArrowRight,
  Quote,
  Instagram,
  Mail,
} from "lucide-react";

import SyntheticWaves from "@/components/SyntheticWaves";
import PageTransition from "@/components/PageTransition";
import HowItWorksSection from "@/components/HowItWorksSection";

/* ───── animation presets ───── */
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

/* ───── Animated Counter ───── */
const Counter = ({ end, suffix = "", prefix = "" }: { end: number; suffix?: string; prefix?: string }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 2000;
    const step = Math.ceil(end / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, end]);

  return (
    <span ref={ref}>
      {prefix}{count}{suffix}
    </span>
  );
};

/* ───── Custom Icons ───── */
const CustomerServiceIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth="1.5">
    <path d="M17 11.8045C17 11.4588 17 11.286 17.052 11.132C17.2032 10.6844 17.6018 10.5108 18.0011 10.3289C18.45 10.1244 18.6744 10.0222 18.8968 10.0042C19.1493 9.98378 19.4022 10.0382 19.618 10.1593C19.9041 10.3198 20.1036 10.6249 20.3079 10.873C21.2513 12.0188 21.7229 12.5918 21.8955 13.2236C22.0348 13.7334 22.0348 14.2666 21.8955 14.7764C21.6438 15.6979 20.8485 16.4704 20.2598 17.1854C19.9587 17.5511 19.8081 17.734 19.618 17.8407C19.4022 17.9618 19.1493 18.0162 18.8968 17.9958C18.6744 17.9778 18.45 17.8756 18.0011 17.6711C17.6018 17.4892 17.2032 17.3156 17.052 16.868C17 16.714 17 16.5412 17 16.1955V11.8045Z"/>
    <path d="M7 11.8046C7 11.3694 6.98778 10.9782 6.63591 10.6722C6.50793 10.5609 6.33825 10.4836 5.99891 10.329C5.55001 10.1246 5.32556 10.0224 5.10316 10.0044C4.43591 9.9504 4.07692 10.4058 3.69213 10.8732C2.74875 12.019 2.27706 12.5919 2.10446 13.2237C1.96518 13.7336 1.96518 14.2668 2.10446 14.7766C2.3562 15.6981 3.15152 16.4705 3.74021 17.1856C4.11129 17.6363 4.46577 18.0475 5.10316 17.996C5.32556 17.978 5.55001 17.8757 5.99891 17.6713C6.33825 17.5167 6.50793 17.4394 6.63591 17.3281C6.98778 17.0221 7 16.631 7 16.1957V11.8046Z"/>
    <path d="M20 10.5V9C20 5.13401 16.4183 2 12 2C7.58172 2 4 5.13401 4 9V10.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 17.5C20 22 16 22 12 22" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const WorkflowIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} stroke="currentColor" strokeWidth="1.5">
    <path d="M9 5C9 3.58579 9 2.87868 9.43934 2.43934C9.87868 2 10.5858 2 12 2C13.4142 2 14.1213 2 14.5607 2.43934C15 2.87868 15 3.58579 15 5C15 6.41421 15 7.12132 14.5607 7.56066C14.1213 8 13.4142 8 12 8C10.5858 8 9.87868 8 9.43934 7.56066C9 7.12132 9 6.41421 9 5Z"/>
    <path d="M2 19C2 17.5858 2 16.8787 2.43934 16.4393C2.87868 16 3.58579 16 5 16C6.41421 16 7.12132 16 7.56066 16.4393C8 16.8787 8 17.5858 8 19C8 20.4142 8 21.1213 7.56066 21.5607C7.12132 22 6.41421 22 5 22C3.58579 22 2.87868 22 2.43934 21.5607C2 21.1213 2 20.4142 2 19Z"/>
    <path d="M16 19C16 17.5858 16 16.8787 16.4393 16.4393C16.8787 16 17.5858 16 19 16C20.4142 16 21.1213 16 21.5607 16.4393C22 16.8787 22 17.5858 22 19C22 20.4142 22 21.1213 21.5607 21.5607C21.1213 22 20.4142 22 19 22C17.5858 22 16.8787 22 16.4393 21.5607C16 21.1213 16 20.4142 16 19Z"/>
    <path d="M12 8V9M12 9C12 9.93188 12 10.3978 12.1776 10.7654C12.4144 11.2554 12.8687 11.6448 13.4404 11.8478C13.8692 12 14.4128 12 15.5 12C16.5872 12 17.1308 12 17.5596 12.1522C18.1313 12.3552 18.5856 12.7446 18.8224 13.2346C19 13.6022 19 14.0681 19 15V16M12 9C12 9.93188 12 10.3978 11.8224 10.7654C11.5856 11.2554 11.1313 11.6448 10.5596 11.8478C10.1308 12 9.5872 12 8.5 12C7.4128 12 6.8692 12 6.44041 12.1522C5.86867 12.3552 5.41443 12.7446 5.17761 13.2346C5 13.6022 5 14.0681 5 15V16" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ───── Data ───── */
const services = [
  {
    icon: CustomerServiceIcon,
    title: "Voice AI Agents",
    desc: "24/7 chat and voice agents that answer questions, fix problems, and keep customers happy. Around the clock, no extra staff.",
  },
  {
    icon: CalendarCheck,
    title: "Chatbot AI Agents",
    desc: "Website chatbot agents can answer all your website visiting customers queries and help them schedule calls.",
  },
  {
    icon: Target,
    title: "Lead Qualification Agents",
    desc: "Your sales team only talks to people ready to buy. The AI handles everything before that conversation.",
  },
  {
    icon: WorkflowIcon,
    title: "Internal Workflow Agents",
    desc: "The tasks your team does on repeat — data entry, follow-ups, internal updates — handled automatically.",
  },
];


const metrics = [
  { end: 85, suffix: "%", label: "Reduction in response time" },
  { end: 3, suffix: "x", label: "More leads captured" },
  { end: 500, suffix: "+", label: "Hours saved monthly" },
];

const testimonials = [
  {
    quote: "Since implementing their AI agent, we've cut our response time in half and our team finally has breathing room.",
    name: "Sarah Mitchell",
    role: "Operations Manager",
    company: "BrightFit Studios",
  },
  {
    quote: "The booking agent alone has saved us 20+ hours a week. It just works — no babysitting required.",
    name: "James Hartley",
    role: "Owner",
    company: "Hartley Trades & Services",
  },
  {
    quote: "Professional, responsive, and they actually understand small business. Couldn't recommend them more.",
    name: "Priya Kapoor",
    role: "Clinic Director",
    company: "Kapoor Wellness",
  },
];

const logos = [
  { name: "Gmail", logo: gmailLogo, size: "h-10 w-10" },
  { name: "Salesforce", logo: salesforceLogo },
  { name: "Zendesk", logo: zendeskLogo },
  { name: "Calendly", logo: calendlyLogo },
  { name: "Office 365", logo: office365Logo },
  { name: "Monday.com", logo: mondayLogo, size: "h-12 w-12" },
  { name: "HubSpot", logo: hubspotLogo, size: "h-14 w-14" },
];

/* ───── Component ───── */
const Overview = () => {
  return (
    <PageTransition>
      <div className="min-h-screen bg-background text-foreground overflow-hidden">
        {/* Spacer for fixed navbar */}
        <div className="h-20" />

        {/* ─── 1. HERO ─── */}
        <section className="relative min-h-[540px] lg:min-h-[620px] py-16 lg:py-20 overflow-hidden flex items-center">
          {/* Mesh gradient background */}
          {/* Synthetic wave background */}
          <SyntheticWaves />
          {/* Mesh gradient background */}
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-primary/5 blur-[120px] animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-primary/8 blur-[100px] animate-pulse" style={{ animationDelay: "1s" }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-primary/3 blur-[160px] animate-pulse" style={{ animationDelay: "2s" }} />
          </div>

          <div className="container mx-auto px-4 sm:px-6 w-full">
            <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center">
              {/* Left - Text */}
              <motion.div {...fadeUp} className="text-left">
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-sm font-medium tracking-widest uppercase text-primary mb-6 font-['Montserrat']"
                >
                  AI Agents for Local Businesses
                </motion.p>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-['Montserrat'] leading-tight mb-6 whitespace-pre-line">
                  Automate Tasks{"\n"}
                  <span className="text-gradient-orange">Focus on Growth</span>
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mb-10 font-['Montserrat'] font-normal whitespace-pre-line">
                  Your competitors aren't working harder — they've automated.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/book-demo"
                    className="inline-flex items-center justify-center h-12 sm:h-14 px-6 sm:px-10 rounded-xl bg-primary text-primary-foreground font-semibold text-base sm:text-lg shadow-lg hover:shadow-[0_0_30px_hsla(27,95%,64%,0.4)] hover:-translate-y-0.5 transition-all duration-300 font-['Montserrat'] whitespace-normal text-center"
                  >
                    Book a Free Discovery Call
                  </Link>
                  <button
                    onClick={() => document.getElementById('what-we-build')?.scrollIntoView({ behavior: 'smooth' })}
                    className="inline-flex items-center justify-center h-12 sm:h-14 px-6 sm:px-10 rounded-xl border border-border bg-transparent text-foreground font-medium text-base sm:text-lg hover:border-primary hover:text-primary transition-all duration-300 font-['Montserrat'] whitespace-normal text-center"
                  >
                    Learn More
                  </button>
                </div>
              </motion.div>

              {/* Right - AI Agents Embed */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="hidden lg:block"
              >
                <div className="relative w-full h-[500px]">
                  <iframe
                    src="/four-agents-hero.html"
                    className="w-full h-full rounded-xl"
                    style={{ background: 'transparent', border: 'none' }}
                    title="AI Agents Demo"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── 2. TRUSTED BY ─── */}
        <section className="py-12 border-y border-border/40">
          <div className="container mx-auto px-4 sm:px-6">
            <p className="text-center text-sm text-muted-foreground tracking-widest uppercase mb-8 font-['Montserrat']">
              OUR INTEGRATIONS
            </p>
            <div className="relative overflow-hidden">
              <motion.div
                className="flex gap-12 items-center w-max"
                animate={{ x: ["0%", "-50%"] }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear", repeatType: "loop" }}
              >
                {[...logos, ...logos, ...logos, ...logos].map((item, i) => (
                  <div
                    key={i}
                    className="flex-shrink-0 px-6 py-3 flex items-center gap-3"
                  >
                    <img
                      src={item.logo}
                      alt={item.name}
                      className={`${(item as any).size || "h-8 w-8"} rounded object-contain opacity-80`}
                    />
                    <span className="text-muted-foreground/80 font-medium text-sm tracking-wide font-['Montserrat'] whitespace-nowrap">
                      {item.name}
                    </span>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── 3. WHAT WE BUILD ─── */}
        <section id="what-we-build" className="py-14 lg:py-32 relative overflow-hidden">
          {/* Ambient glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-primary/[0.03] blur-[150px] pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <motion.div {...fadeUp} className="mb-16 max-w-[620px]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-[2px] bg-primary" />
                <span className="text-primary text-xs font-semibold tracking-[0.2em] uppercase font-['Montserrat']">
                  Our Solutions
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-normal font-['Montserrat'] leading-[1.08] tracking-tight mb-4">
                What We <span className="text-gradient-orange">Build</span>
              </h2>
              <p className="text-[15px] text-muted-foreground font-['Montserrat'] font-light leading-relaxed">
                Intelligent AI agents designed for the real challenges local businesses face every day.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 w-full">
              {services.map((s, i) => {
                const num = String(i + 1).padStart(2, "0");
                const spanTwo = i === 0 || i === 3;
                return (
                  <motion.div
                    key={s.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    className={`group relative rounded-2xl bg-[hsl(0,0%,7%)] border border-white/[0.06] p-6 sm:p-8 md:p-10 overflow-hidden cursor-default transition-all duration-500 hover:-translate-y-1 hover:border-primary/15 ${spanTwo ? "md:col-span-2" : ""}`}
                    onMouseMove={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      e.currentTarget.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
                      e.currentTarget.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
                    }}
                    style={{
                      background: "radial-gradient(600px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), hsla(27,95%,64%,0.04), transparent 40%), hsl(0,0%,7%)",
                    }}
                  >
                    {/* Top glow line */}
                    <div className="absolute top-0 left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-primary/0 to-transparent group-hover:via-primary/40 transition-all duration-500" />

                    {/* Decorative number */}
                    <span className="absolute top-6 right-8 text-[60px] sm:text-[80px] md:text-[100px] font-bold font-['Montserrat'] leading-none text-white/[0.025] group-hover:text-primary/[0.08] transition-colors duration-500 select-none pointer-events-none">
                      {num}
                    </span>

                    {/* Icon */}
                    <div className="w-[52px] h-[52px] rounded-[14px] bg-gradient-to-br from-primary/[0.12] to-primary/[0.04] flex items-center justify-center mb-6 group-hover:shadow-[0_0_20px_hsla(27,95%,64%,0.15)] transition-shadow duration-500">
                      <s.icon className="h-6 w-6 text-primary" />
                    </div>

                    <h3 className="text-[22px] md:text-[26px] font-normal font-['Montserrat'] mb-3 text-foreground">
                      {s.title}
                    </h3>
                    <p className="text-[15px] text-muted-foreground font-['Montserrat'] font-light leading-relaxed max-w-[420px]">
                      {s.desc}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── 4. HOW IT WORKS ─── */}
        <HowItWorksSection />

        {/* ─── 5. RESULTS / METRICS ─── */}
        <section className="py-14 lg:py-32 relative overflow-hidden">
          {/* Ambient glow */}
          <div className="absolute top-[10%] left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-[radial-gradient(ellipse,hsla(27,95%,64%,0.15),transparent_70%)] pointer-events-none opacity-25" />

          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <motion.div {...fadeUp} className="mb-16 md:mb-20 max-w-[620px]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-[2px] bg-primary" />
                <span className="text-primary text-xs font-semibold tracking-[0.2em] uppercase font-['Montserrat']">
                  Impact
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-normal font-['Montserrat'] leading-[1.08] tracking-tight mb-4">
                Real <span className="text-gradient-orange">Results</span>
              </h2>
              <p className="text-[15px] text-muted-foreground font-['Montserrat'] font-light leading-relaxed">
                Numbers that speak for themselves.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {metrics.map((m, i) => (
                <motion.div
                  key={m.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  className="group relative rounded-2xl bg-[hsl(0,0%,7%)] border border-white/[0.06] p-8 sm:p-[52px_40px_48px] overflow-hidden cursor-default transition-all duration-500 hover:-translate-y-1 hover:border-primary/15"
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    e.currentTarget.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
                    e.currentTarget.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
                  }}
                  style={{
                    background: "radial-gradient(400px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), hsla(27,95%,64%,0.08), transparent 60%), hsl(0,0%,7%)",
                  }}
                >
                  {/* Top glow line */}
                  <div className="absolute top-0 left-[25%] right-[25%] h-[1px] bg-gradient-to-r from-transparent via-primary/0 to-transparent group-hover:via-primary/50 group-hover:left-[10%] group-hover:right-[10%] transition-all duration-500" />

                  {/* Ghost watermark */}
                  <span className="absolute bottom-[-16px] right-6 text-[140px] font-bold font-['Montserrat'] leading-none text-white/[0.015] group-hover:text-primary/[0.03] transition-colors duration-500 select-none pointer-events-none">
                    {m.end}{m.suffix === "x" ? "x" : ""}
                  </span>

                  {/* Value */}
                  <div className="relative z-10">
                    <div className="text-[clamp(52px,5vw,68px)] font-bold text-primary font-['Montserrat'] leading-none mb-2 tracking-[-2px] inline-block">
                      <Counter end={m.end} />
                      <span className="text-[0.55em] font-semibold tracking-normal align-top ml-0.5 opacity-85">
                        {m.suffix}
                      </span>
                    </div>
                    <div className="w-10 h-[2px] bg-primary/50 my-5 rounded-sm group-hover:w-[60px] group-hover:opacity-80 transition-all duration-400" />
                    <p className="text-[15px] text-muted-foreground font-['Montserrat'] font-normal leading-relaxed tracking-[0.2px]">
                      {m.label}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── 7. CTA BANNER ─── */}
        <section id="cta" className="py-14 lg:py-[120px] px-6 md:px-[60px] relative">
          <div className="max-w-[1320px] mx-auto">
            <motion.div
              {...fadeUp}
              className="cta-container relative bg-[hsl(0,0%,7%)] border border-white/[0.06] rounded-[28px] py-12 sm:py-16 md:py-[100px] px-6 sm:px-10 md:px-[80px] overflow-hidden text-center"
            >
              {/* Floating particles */}
              <div className="cta-particle cta-particle--1" />
              <div className="cta-particle cta-particle--2" />
              <div className="cta-particle cta-particle--3" />
              <div className="cta-particle cta-particle--4" />

              {/* Corner accents */}
              <div className="absolute top-5 left-5 w-12 h-12 opacity-15 pointer-events-none">
                <svg viewBox="0 0 48 48" className="w-full h-full"><path d="M0 24V0h24" fill="none" stroke="hsl(var(--primary) / 0.4)" strokeWidth="1"/></svg>
              </div>
              <div className="absolute top-5 right-5 w-12 h-12 opacity-15 pointer-events-none -scale-x-100">
                <svg viewBox="0 0 48 48" className="w-full h-full"><path d="M0 24V0h24" fill="none" stroke="hsl(var(--primary) / 0.4)" strokeWidth="1"/></svg>
              </div>
              <div className="absolute bottom-5 left-5 w-12 h-12 opacity-15 pointer-events-none -scale-y-100">
                <svg viewBox="0 0 48 48" className="w-full h-full"><path d="M0 24V0h24" fill="none" stroke="hsl(var(--primary) / 0.4)" strokeWidth="1"/></svg>
              </div>
              <div className="absolute bottom-5 right-5 w-12 h-12 opacity-15 pointer-events-none scale-[-1]">
                <svg viewBox="0 0 48 48" className="w-full h-full"><path d="M0 24V0h24" fill="none" stroke="hsl(var(--primary) / 0.4)" strokeWidth="1"/></svg>
              </div>

              {/* Label */}
              <div className="flex items-center gap-3 mb-8 relative z-10 justify-center">
                <div className="w-8 h-[2px] bg-primary" />
                <span className="text-primary text-xs font-semibold tracking-[0.2em] uppercase font-['Montserrat']">
                  Get Started
                </span>
                <div className="w-8 h-[2px] bg-primary" />
              </div>

              {/* Title */}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-normal font-['Montserrat'] leading-[1.08] tracking-tight mb-7 max-w-[700px] mx-auto relative z-10">
                Ready to put AI to work{" "}
                <span className="text-gradient-orange">for your business?</span>
              </h2>

              {/* Subtitle */}
              <p className="text-[15px] leading-relaxed text-muted-foreground font-light font-['Montserrat'] max-w-[480px] mx-auto mb-[52px] relative z-10">
                No commitment. No jargon. Just a conversation about what's possible.
              </p>

              {/* Button */}
              <div className="relative z-10 flex justify-center">
                <Link
                  to="/book-demo"
                  className="cta-main-btn inline-flex items-center gap-3 py-5 px-11 max-[420px]:px-7 max-[420px]:text-[13px] font-['Montserrat'] text-[15px] font-semibold tracking-[0.5px] text-primary-foreground bg-primary rounded-xl relative overflow-hidden transition-all duration-[0.4s] ease-[cubic-bezier(0.23,1,0.32,1)] hover:-translate-y-0.5 hover:shadow-[0_8px_40px_hsl(var(--primary)/0.3),0_2px_12px_hsl(var(--primary)/0.2)] whitespace-nowrap"
                >
                  Book a Free Discovery Call
                  <ArrowRight className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>

              {/* Hint */}
              <p className="mt-5 text-[13px] text-muted-foreground font-light font-['Montserrat'] relative z-10 inline-flex items-center gap-1">
                <svg viewBox="0 0 24 24" className="w-[14px] h-[14px] stroke-current fill-none" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                </svg>
                Usually a 30-minute chat
              </p>
            </motion.div>

            {/* Divider */}
            <div className="w-px h-16 mx-auto mt-14 bg-gradient-to-b from-white/[0.06] to-transparent" />

            {/* Back to top */}
            <div className="text-center mt-7">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="inline-flex items-center gap-2 text-[13px] font-normal text-muted-foreground hover:text-primary transition-colors duration-300 tracking-[0.5px] font-['Montserrat'] group"
              >
                <svg viewBox="0 0 24 24" className="w-[14px] h-[14px] stroke-current fill-none transition-transform duration-300 group-hover:-translate-y-[3px]" strokeWidth="2">
                  <path d="M12 19V5M5 12l7-7 7 7"/>
                </svg>
                Back to Top
              </button>
            </div>
          </div>
        </section>

      </div>
    </PageTransition>
  );
};

export default Overview;
